import React, { useState, useEffect } from 'react';
import Pagination from '@mui/material/Pagination';
import EntityColumns from './entityTable/EntityColumns';
import ProductsColumns from './commodityTable/CommodityColumns';
import CyberColumns from './cyberTable/CyberColumns';
import EntityRow from './entityTable/EntityRow';
import ProductRow from './commodityTable/CommodityRow';
import CyberRow from './cyberTable/CyberRow';
import { exportToCSV } from '../../../components/Common/CSVManage';
import { Typography } from '@mui/material';
import {
  NextButton,
  StyledInput,
} from '../../../components/Common/MUIStyledComponents';
import Modal from 'react-modal';

interface ISingleScreeningProps {
  results: any;
  page: number;
  setPage: any;
  handleSubmit: any;
  type: string;
  setShowFullRecord: any;
  cyberHeaders: string[];
  getCSVResults?: any;
  setTicketNameOpen: any;
  inputs: any;
  handleOpenModal?: any;
}
interface ScreeningResult {
  'Name of Entity': string;
  'Entity Country': string;
  'List Name': string;
  Agency?: string;
  'Additional Remarks'?: string;
  'Date Updated on BITE Platform'?: string;
}

const WorkflowSingleScreeningResults: React.FC<ISingleScreeningProps> = ({
  setShowFullRecord,
  type,
  handleSubmit,
  setPage,
  page,
  results,
  cyberHeaders,
  getCSVResults,
}) => {
  const totalResults = results?.data[0]?.result?.body?.hits?.total?.value;
  const resultsPerPage = 50;
  const [ pageCount, setPageCount ] = useState<number>(0);
  const [ fileName, setFileName ] = useState<string>('');
  const [ fileNameOpen, setFileNameOpen ] = useState(false);
  const [ hitsData, setHitsData ] = useState<any>(
    results?.data[0]?.result?.body?.hits?.hits,
  );
  const [ sortConfig, setSortConfig ] = useState<{
    key: keyof ScreeningResult;
    direction: 'asc' | 'desc';
  }>({
    key: 'Name of Entity',
    direction: 'asc',
  });

  const handlePageChange = (e: any, new_page: number) => {
    const handledPage = new_page - 1;
    setPage(handledPage);
    handleSubmit(handledPage);
  };

  const entHeaders = [
    'Name of Entity',
    'Entity Country',
    'List Name',
    'Agency',
    'Additional Remarks',
    'Date Updated',
  ];
  const comHeaders = [
    'Product Name',
    'HS Code',
    'Source Country',
    'Agency',
    'List Name',
    'Date Updated',
  ];
  const tradeAlertsHeaders = [
    'Intervention Type',
    'HS Code',
    'Implementing Jurisdictions',
    'Risk Country',
    'GTA Evaluation',
  ];
  const cveHeaders = [
    'CVE ID',
    'Vendor Project',
    'Product',
    'Vulnerability Name',
    'Required Action',
  ];
  const markHeaders = [
    'Market Name',
    'Description',
    'Website',
    'Market Location',
  ];

  const handleCSV = async() => {
    const res = await getCSVResults(0);
    if (res[0].result?.body?.hits?.hits.length >= 1) {
      exportToCSV(
        fileName,
        [ ...res[0].result?.body?.hits?.hits ],
        type === 'Entities' ?
          entHeaders :
          type === 'Products' ?
            comHeaders :
            cyberHeaders?.includes('CVE ID') ?
              cveHeaders :
              cyberHeaders?.includes('Intervention Type') ?
                tradeAlertsHeaders :
                markHeaders,
        type === 'Entities' ?
          'entity' :
          type === 'Products' ?
            'commodity' :
            cyberHeaders?.includes('CVE ID') ?
              'CVE' :
              cyberHeaders?.includes('Intervention Type') ?
                'TradeAlerts' :
                'Markets',
      );
    }
  };

  const handleFileNameChange = (e: any) => {
    setFileName(e.target.value);
  };

  const handleFileNameClose = () => {
    setFileNameOpen(false);
    setFileName('');
  };

  useEffect(() => {
    const totalPages = Math.ceil(totalResults / resultsPerPage);
    if (totalPages >= 200) {
      setPageCount(199);
    } else if (totalPages <= 199) {
      setPageCount(totalPages);
    }
  }, [ results ]);

  useEffect(() => {
    if (results.data[0].result.body.hits.hits.length === 0) {
      setPage(0);
    }
  }, [ results.data[0] ]);

  const fieldMap: Record<string, string> = {
    'nameOfEntity': 'Name of Entity',
    'nameOfCountry': 'Entity Country',
    'listName': 'List Name',
    'agency': 'Agency',
    'addRemarks': 'Additional Remarks',
    'dateUpdated': 'Date Updated on BITE Platform',
    'productName': 'Product Name',
    'hsCode': 'HS Code',
    'sourceCountry': 'Source Country',
    'CVE ID': 'CVE ID',
    'Vendor Project': 'Vendor Project',
    'Product': 'Product',
    'Vulnerability Name': 'Vulnerability Name',
    'Required Action': 'Required Action',
    'Market Name': 'Market Name',
    'Description': 'Description',
    'Website': 'Website',
    'Market Location': 'Market Location',
  };

  const compareValues = (
    aVal: string,
    bVal: string,
    direction: 'asc' | 'desc',
  ) => {
    const aIsEmpty = !aVal?.trim();
    const bIsEmpty = !bVal?.trim();

    if (aIsEmpty && !bIsEmpty) {
      return direction === 'asc' ? -1 : 1;
    }
    if (!aIsEmpty && bIsEmpty) {
      return direction === 'asc' ? 1 : -1;
    }
    if (aIsEmpty && bIsEmpty) {
      return 0;
    }

    return direction === 'asc' ?
      aVal.localeCompare(bVal) :
      bVal.localeCompare(aVal);
  };

  const sortData = (key: string) => {
    const typedKey = key as keyof ScreeningResult;
    const sortedData = [ ...hitsData ];

    const field = fieldMap[key];
    if (!field) {
      return;
    }

    const direction = sortConfig.direction;
    sortedData.sort((a, b) =>
      compareValues(
        a._source?.[field] || '',
        b._source?.[field] || '',
        direction,
      ),
    );

    // Toggle sort direction
    const newDirection = direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: typedKey, direction: newDirection });
    setHitsData(sortedData);
  };

  return (
    <div className='screening-results-container'>
      <div className='screening-results-header'>
        <div className='screening-results-header-group'>
          <div className='screening-results-header-frame'>
            <div className='screening-results-matches'>
              <div className='screening-results-matches-inner'>
                <div className='screening-matches-text'>
                  {totalResults ? totalResults : 0} results found
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {type === 'Entities' &&
        !results.isLoading &&
        results.data.length >= 1 && (
        <>
          <EntityColumns
            showFilters={false}
            sortData={sortData}
            sortConfig={sortConfig}
          />
          <div className='screening-results-table-container'>
            {hitsData?.map((hit: any, index: number) => (
              <EntityRow
                showFilters={false}
                setShowFullRecord={setShowFullRecord}
                hit={hit}
                key={index}
              />
            ))}
          </div>
        </>
      )}
      {type === 'Products' &&
        !results.isLoading &&
        results.data.length >= 1 && (
        <>
          <ProductsColumns
            showFilters={false}
            sortData={sortData}
            sortConfig={sortConfig}
          />
          <div className='screening-results-table-container'>
            {hitsData?.map((hit: any, index: number) => (
              <ProductRow
                showFilters={false}
                setShowFullRecord={setShowFullRecord}
                hit={hit}
                key={index}
              />
            ))}
          </div>
        </>
      )}
      {type === 'Cyber' && !results.isLoading && results.data.length >= 1 && (
        <>
          <CyberColumns
            showFilters={false}
            headers={cyberHeaders}
            sortData={sortData}
            sortConfig={sortConfig}
          />
          <div className='screening-results-table-container'>
            {hitsData?.map((hit: any, index: number) => (
              <CyberRow
                showFilters={false}
                setShowFullRecord={setShowFullRecord}
                hit={hit}
                key={index}
                tab={
                  cyberHeaders.includes('CVE ID') ?
                    'CVE' :
                    cyberHeaders?.includes('Intervention Type') ?
                      'Trade' :
                      'Markets'
                }
              />
            ))}
          </div>
        </>
      )}

      {!results.isLoading &&
        results?.data[0]?.result?.body?.hits?.total?.value === 0 && (
        <div className='screening-results-table-container'>
          <div
            className='no-screening-results-text'
            style={{
              width: '100%',
              padding: '24px 16px',
              boxSizing: 'border-box',
              textAlign: 'center',
            }}
          >
              No results found. Please try another search
          </div>
        </div>
      )}
      {totalResults > 0 && (
        <div className='screening-results-footer'>
          <div className='screening-results-pagination-text'>
            Page {page + 1} of {pageCount}
          </div>
          <div className='screening-results-footer-action-group'>
            <div className='hb-pagination'>
              <Pagination
                onChange={handlePageChange}
                size='large'
                count={pageCount}
                shape='rounded'
                variant='text'
                page={page + 1}
              />
            </div>
          </div>
        </div>
      )}
      <Modal
        isOpen={fileNameOpen}
        onRequestClose={handleFileNameClose}
        ariaHideApp={false}
      >
        <div className='bite-score-file-modal'>
          <div className='display-flex justify-between width-100'>
            <Typography variant='h5'>File Name:</Typography>
          </div>
          <Typography
            sx={{
              color: 'rgba(189, 190, 193, 0.87)',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineheight: '150%',
            }}
            variant='h6'
          >
            This will download the first 200 results
          </Typography>
          <div className='display-flex justify-end width-100'>
            <StyledInput
              value={fileName}
              onChange={handleFileNameChange}
              variant='outlined'
              className='margin-right-20px'
            />
            <NextButton
              variant='contained'
              color='primary'
              onClick={() => {
                handleCSV();
                handleFileNameClose();
              }}
            >
              Download
            </NextButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WorkflowSingleScreeningResults;
